import React, { useEffect, useState } from 'react'

import { RouteParams, routes } from '@sketch/modules-common'
import { Button, Transition, useModalContext } from '@sketch/components'

import { useSelectionContext } from '../SelectionContext/SelectionContext'
import {
  Wrapper,
  Text,
  ActionButton,
  StyledDivider,
  DocumentIcon,
  ActionButtonWrapper,
  SelectionBottomSinkWrapper,
} from './SelectionBottomSink.styles'

import { ReactComponent as ArrowsRightLeftIcon } from '@sketch/icons/img-arrows-right-left-17'
import { ReactComponent as BoxIcon } from '@sketch/icons/img-box-17'
import { ReactComponent as TrashEmptyIcon } from '@sketch/icons/trash-empty-17'

import { useStorageItemsCount } from '../../hooks/useStorageItemsCount'

import { ProjectFragment, ShareListItemFragment } from '@sketch/gql-types'
import { matchPath, useLocation } from 'react-router'
import { MoveStorageItemModal } from '../../modals'
import { useGetProjects } from '../../../projects/operations'

type StorageItem =
  | Pick<ShareListItemFragment, 'identifier' | '__typename'>
  | Pick<ProjectFragment, 'identifier' | '__typename'>

interface BaseSelectionBottomSinkProps {
  className?: string
  workspaceIdentifier: string
  selectedItems: StorageItem[]
  onClearSelection: () => void
}

const BaseSelectionBottomSink = (props: BaseSelectionBottomSinkProps) => {
  const { selectedItems, className, onClearSelection, workspaceIdentifier } =
    props

  const { message } = useStorageItemsCount(selectedItems)
  const { showModal } = useModalContext()
  const location = useLocation()

  // This should be cached, given the sink shows in routes where projects are already loaded
  const projects = useGetProjects({ workspaceId: workspaceIdentifier })

  const isProjectRoute = matchPath<RouteParams<'WORKSPACE_PROJECT'>>(
    location.pathname,
    routes.WORKSPACE_PROJECT.template()
  )

  const isDraftsRoute = matchPath(
    location.pathname,
    routes.WORKSPACE_DRAFTS.template()
  )

  const isTrashRoute = matchPath(
    location.pathname,
    routes.WORKSPACE_TRASH.template()
  )

  const isArchiveRoute = matchPath(
    location.pathname,
    routes.WORKSPACE_ARCHIVE.template()
  )

  const parentProjectIdentifier =
    (isDraftsRoute
      ? projects.draftsProject?.entries[0].identifier
      : isProjectRoute?.params.projectId) || null

  return (
    <Wrapper className={className}>
      <DocumentIcon />
      <Text textStyle="copy.primary.standard.E" as="span">
        {message} Selected
      </Text>

      <ActionButtonWrapper>
        {!isArchiveRoute && !isTrashRoute && (
          <ActionButton
            description="Move..."
            onClick={() => {
              showModal(MoveStorageItemModal, {
                items: selectedItems,
                workspaceIdentifier,
                parentProjectIdentifier,
                action: 'move',
                originType: 'standard',
              })
            }}
          >
            <ArrowsRightLeftIcon role="img" />
          </ActionButton>
        )}

        {!isArchiveRoute && !isTrashRoute && (
          <ActionButton description="Archive...">
            <BoxIcon role="img" />
          </ActionButton>
        )}

        {!isTrashRoute && (
          <ActionButton description="Remove...">
            <TrashEmptyIcon role="img" />
          </ActionButton>
        )}
      </ActionButtonWrapper>

      <StyledDivider />

      <Button variant="tertiary" size="32" onClick={onClearSelection}>
        Clear
      </Button>
    </Wrapper>
  )
}

interface SelectionBottomSinkProps {
  className?: string
  workspaceIdentifier: string
}

export const SelectionBottomSink = (props: SelectionBottomSinkProps) => {
  const { className, workspaceIdentifier } = props

  const { selectedItems, clearSelection } = useSelectionContext()

  return (
    <BaseSelectionBottomSink
      className={className}
      workspaceIdentifier={workspaceIdentifier}
      selectedItems={selectedItems}
      onClearSelection={clearSelection}
    />
  )
}

interface SelectionBottomSinkProps {
  className?: string
  workspaceIdentifier: string
}

export const AnimatedSelectionBottomSink = (
  props: SelectionBottomSinkProps
) => {
  const { className, workspaceIdentifier } = props

  const { selectedItems, clearSelection, hasSelectedItems } =
    useSelectionContext()

  const [cachedSelectedItems, setCachedSelectedItems] = useState(selectedItems)

  useEffect(() => {
    if (hasSelectedItems) {
      setCachedSelectedItems(selectedItems)
    }
  }, [hasSelectedItems, selectedItems])

  return (
    <Transition timeout={200} in={hasSelectedItems} mountOnEnter unmountOnExit>
      {state => (
        <SelectionBottomSinkWrapper data-animation-state={state}>
          <BaseSelectionBottomSink
            className={className}
            workspaceIdentifier={workspaceIdentifier}
            selectedItems={cachedSelectedItems}
            onClearSelection={clearSelection}
          />
        </SelectionBottomSinkWrapper>
      )}
    </Transition>
  )
}
